import React, { createContext, useState, useEffect, useCallback, useMemo } from "react";

export const AppContext = createContext();

const isBrowser = typeof window !== "undefined";
const AUTH_SESSION_KEY = "factusysdr_auth_session";

const emptyUser = {
  email: null,
  username: null,
  isSuperuser: false,
  isAdmin: false,
  isVendedor: false,
  isCliente: false,
  isLoggedIn: false,
  accountType: "",
};

const normalizeType = (value) => {
  const type = String(value || "").toLowerCase();

  if (["client", "cliente", "customer", "cliente_cuenta"].includes(type)) {
    return "client";
  }

  if (
    [
      "admin",
      "staff",
      "seller",
      "vendedor",
      "internal",
      "interno",
      "superadmin",
      "super_admin",
      "usuario",
    ].includes(type)
  ) {
    return "admin";
  }

  return "";
};

const getRedirectByType = (type) => {
  if (type === "admin") return "/admin-panel";
  if (type === "client") return "/user-panel";
  return "/";
};

const getLabelByType = (type) => {
  if (type === "admin") return "Cuenta administrativa";
  if (type === "client") return "Mi cuenta";
  return "Cuenta";
};

const normalizeAccount = (account, fallbackType = "") => {
  if (!account) return null;

  const rawType =
    account.account_type ||
    account.accountType ||
    account.tipo_cuenta ||
    account.tipoCuenta ||
    account.type ||
    account.role ||
    fallbackType;

  const type = normalizeType(rawType);

  if (!type) return null;

  const access =
    account.access ||
    account.access_token ||
    account.token ||
    account.jwt ||
    account.auth_token ||
    "";

  const refresh =
    account.refresh ||
    account.refresh_token ||
    account.refreshToken ||
    "";

  const user =
    account.user ||
    account.usuario ||
    account.cliente ||
    account.persona ||
    account.profile ||
    {};

  return {
    type,
    label: account.label || account.name || getLabelByType(type),
    access,
    refresh,
    user,
    permissions: account.permissions || account.permisos || [],
    redirectTo: account.redirectTo || account.redirect_to || getRedirectByType(type),
    raw: account,
  };
};

const extractAccounts = (payload) => {
  if (!payload) return [];

  if (Array.isArray(payload.accounts)) {
    return payload.accounts.map((item) => normalizeAccount(item)).filter(Boolean);
  }

  if (Array.isArray(payload.cuentas)) {
    return payload.cuentas.map((item) => normalizeAccount(item)).filter(Boolean);
  }

  const accounts = [];

  if (payload.admin_account) {
    const item = normalizeAccount(payload.admin_account, "admin");
    if (item) accounts.push(item);
  }

  if (payload.adminAccount) {
    const item = normalizeAccount(payload.adminAccount, "admin");
    if (item) accounts.push(item);
  }

  if (payload.client_account) {
    const item = normalizeAccount(payload.client_account, "client");
    if (item) accounts.push(item);
  }

  if (payload.clientAccount) {
    const item = normalizeAccount(payload.clientAccount, "client");
    if (item) accounts.push(item);
  }

  if (payload.cliente_account) {
    const item = normalizeAccount(payload.cliente_account, "client");
    if (item) accounts.push(item);
  }

  if (payload.clienteAccount) {
    const item = normalizeAccount(payload.clienteAccount, "client");
    if (item) accounts.push(item);
  }

  if (accounts.length) return accounts;

  const directType =
    payload.account_type ||
    payload.accountType ||
    payload.tipo_cuenta ||
    payload.tipoCuenta ||
    payload.type ||
    payload.role ||
    "";

  const normalizedDirectType = normalizeType(directType);

  if (normalizedDirectType) {
    const item = normalizeAccount(payload, normalizedDirectType);
    return item ? [item] : [];
  }

  if (payload.is_cliente || payload.isClient || payload.cliente) {
    const item = normalizeAccount(payload, "client");
    return item ? [item] : [];
  }

  const item = normalizeAccount(payload, "admin");
  return item ? [item] : [];
};

const getStoredAuthSession = () => {
  if (!isBrowser) return null;

  try {
    const raw = localStorage.getItem(AUTH_SESSION_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);

    if (!parsed?.activeAccount?.type) return null;

    return parsed;
  } catch {
    localStorage.removeItem(AUTH_SESSION_KEY);
    return null;
  }
};

const buildUserObject = (account) => {
  if (!account) return emptyUser;

  const accountUser = account.user || {};
  const type = normalizeType(account.type);

  return {
    email: accountUser.email || accountUser.correo || null,
    username: accountUser.username || accountUser.nombre || accountUser.razon_social || accountUser.nombre_comercial || null,
    isSuperuser: type === "admin" && !!(accountUser.is_superuser || accountUser.isSuperuser),
    isAdmin: type === "admin" && !!(accountUser.is_admin || accountUser.isAdmin || accountUser.is_superuser || accountUser.isSuperuser),
    isVendedor: type === "admin" && !!(accountUser.is_vendedor || accountUser.isVendedor),
    isCliente: type === "client",
    isLoggedIn: true,
    accountType: type,
  };
};

const getStoredUser = () => {
  if (!isBrowser) return emptyUser;

  const session = getStoredAuthSession();

  if (session?.activeAccount) {
    return buildUserObject(session.activeAccount);
  }

  const access = localStorage.getItem("access");
  const email = localStorage.getItem("user_email");
  const username = localStorage.getItem("user_name");
  const isSuperuser = localStorage.getItem("is_superuser") === "true";
  const isAdmin = localStorage.getItem("is_admin") === "true";
  const isVendedor = localStorage.getItem("is_vendedor") === "true";
  const isCliente = localStorage.getItem("is_cliente") === "true";
  const accountType = localStorage.getItem("account_type") || (isCliente ? "client" : "admin");
  const isLoggedIn = localStorage.getItem("is_logged_in") === "true" && !!access;

  return {
    email: email || null,
    username: username || null,
    isSuperuser,
    isAdmin,
    isVendedor,
    isCliente,
    isLoggedIn,
    accountType: isLoggedIn ? accountType : "",
  };
};

const getStoredAccess = () => {
  if (!isBrowser) return "";

  const session = getStoredAuthSession();

  if (session?.activeAccount?.access) {
    return session.activeAccount.access;
  }

  return localStorage.getItem("access") || "";
};

const getStoredRefresh = () => {
  if (!isBrowser) return "";

  const session = getStoredAuthSession();

  if (session?.activeAccount?.refresh) {
    return session.activeAccount.refresh;
  }

  return localStorage.getItem("refresh") || "";
};

const initialUser = getStoredUser();

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(initialUser);
  const [activeAccount, setActiveAccount] = useState(() => {
    const session = getStoredAuthSession();
    return session?.activeAccount || null;
  });
  const [pendingAccounts, setPendingAccounts] = useState([]);
  const [accessToken, setAccessToken] = useState(getStoredAccess);
  const [refreshToken, setRefreshToken] = useState(getStoredRefresh);
  const [sections, setSections] = useState([]);
  const [brands, setBrands] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  const accountType = activeAccount?.type || user.accountType || "";
  const isAuthenticated = !!user.isLoggedIn;
  const isAdminAccount = isAuthenticated && accountType === "admin";
  const isClientAccount = isAuthenticated && accountType === "client";

  const buildUserFromAccount = useCallback((account) => {
    return buildUserObject(account);
  }, []);

  const persistActiveAccount = useCallback(
    (account) => {
      if (!isBrowser || !account) return;

      const payload = buildUserFromAccount(account);

      localStorage.setItem(
        AUTH_SESSION_KEY,
        JSON.stringify({
          activeAccount: account,
          selectedType: account.type,
          savedAt: new Date().toISOString(),
        })
      );

      localStorage.setItem("access", account.access || "");
      localStorage.setItem("refresh", account.refresh || "");
      localStorage.setItem("user_email", payload.email ?? "");
      localStorage.setItem("user_name", payload.username ?? "");
      localStorage.setItem("is_superuser", String(payload.isSuperuser));
      localStorage.setItem("is_admin", String(payload.isAdmin));
      localStorage.setItem("is_vendedor", String(payload.isVendedor));
      localStorage.setItem("is_cliente", String(payload.isCliente));
      localStorage.setItem("is_logged_in", "true");
      localStorage.setItem("account_type", payload.accountType);
    },
    [buildUserFromAccount]
  );

  const clearAuthStorage = useCallback(() => {
    if (!isBrowser) return;

    localStorage.removeItem(AUTH_SESSION_KEY);
    localStorage.removeItem("user_email");
    localStorage.removeItem("user_name");
    localStorage.removeItem("is_superuser");
    localStorage.removeItem("is_admin");
    localStorage.removeItem("is_vendedor");
    localStorage.removeItem("is_cliente");
    localStorage.removeItem("is_logged_in");
    localStorage.removeItem("account_type");
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");
  }, []);

  useEffect(() => {
    if (!isBrowser) return;
    const storedCart = localStorage.getItem("cartItems");
    if (storedCart) setCartItems(JSON.parse(storedCart));
  }, []);

  useEffect(() => {
    if (!isBrowser) return;
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    if (!isBrowser) {
      setAuthLoading(false);
      return;
    }

    const session = getStoredAuthSession();

    if (session?.activeAccount) {
      const account = session.activeAccount;
      setActiveAccount(account);
      setAccessToken(account.access || "");
      setRefreshToken(account.refresh || "");
      setUser(buildUserFromAccount(account));
      setAuthLoading(false);
      return;
    }

    const access = localStorage.getItem("access") || "";
    const refresh = localStorage.getItem("refresh") || "";
    const accountTypeStorage = normalizeType(localStorage.getItem("account_type") || "");

    if (access && accountTypeStorage) {
      const account = {
        type: accountTypeStorage,
        label: getLabelByType(accountTypeStorage),
        access,
        refresh,
        user: {
          email: localStorage.getItem("user_email") || null,
          username: localStorage.getItem("user_name") || null,
          is_superuser: localStorage.getItem("is_superuser") === "true",
          is_admin: localStorage.getItem("is_admin") === "true",
          is_vendedor: localStorage.getItem("is_vendedor") === "true",
          is_cliente: localStorage.getItem("is_cliente") === "true",
        },
        permissions: [],
        redirectTo: getRedirectByType(accountTypeStorage),
        raw: {},
      };

      setActiveAccount(account);
      setAccessToken(access);
      setRefreshToken(refresh);
      setUser(buildUserFromAccount(account));
      persistActiveAccount(account);
      setAuthLoading(false);
      return;
    }

    setUser(emptyUser);
    setActiveAccount(null);
    setPendingAccounts([]);
    setAccessToken("");
    setRefreshToken("");
    setAuthLoading(false);
  }, [buildUserFromAccount, persistActiveAccount]);

  const openDrawer = useCallback(() => {
    setIsCartOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setIsCartOpen(false);
  }, []);

  const toggleDrawer = useCallback(() => {
    setIsCartOpen((prev) => !prev);
  }, []);

  const loginUser = useCallback(
    ({ email, username, isSuperuser, isAdmin, isVendedor, access, refresh }) => {
      const account = {
        type: "admin",
        label: "Cuenta administrativa",
        access: access || "",
        refresh: refresh || "",
        user: {
          email: email || null,
          username: username || null,
          is_superuser: !!isSuperuser,
          is_admin: !!isAdmin,
          is_vendedor: !!isVendedor,
        },
        permissions: [],
        redirectTo: "/admin-panel",
        raw: {},
      };

      setActiveAccount(account);
      setPendingAccounts([]);
      setAccessToken(account.access);
      setRefreshToken(account.refresh);
      setUser(buildUserFromAccount(account));
      persistActiveAccount(account);
    },
    [buildUserFromAccount, persistActiveAccount]
  );

  const loginClient = useCallback(
    ({ email, username, cliente, access, refresh }) => {
      const account = {
        type: "client",
        label: "Mi cuenta",
        access: access || "",
        refresh: refresh || "",
        user: {
          email: email || cliente?.correo || null,
          username: username || cliente?.razon_social || cliente?.nombre_comercial || null,
          ...cliente,
        },
        permissions: [],
        redirectTo: "/user-panel",
        raw: {},
      };

      setActiveAccount(account);
      setPendingAccounts([]);
      setAccessToken(account.access);
      setRefreshToken(account.refresh);
      setUser(buildUserFromAccount(account));
      persistActiveAccount(account);
    },
    [buildUserFromAccount, persistActiveAccount]
  );

  const startLoginSession = useCallback(
    (payload) => {
      const accounts = extractAccounts(payload);

      if (!accounts.length) {
        setPendingAccounts([]);
        setActiveAccount(null);
        setAccessToken("");
        setRefreshToken("");
        setUser(emptyUser);
        clearAuthStorage();

        return {
          ok: false,
          requiresSelection: false,
          redirectTo: "",
          accounts: [],
          message: "No se pudo identificar el tipo de cuenta.",
        };
      }

      if (accounts.length > 1) {
        setPendingAccounts(accounts);
        setActiveAccount(null);
        setAccessToken("");
        setRefreshToken("");
        setUser(emptyUser);
        clearAuthStorage();

        return {
          ok: true,
          requiresSelection: true,
          redirectTo: "",
          accounts,
          message: "Seleccione con qué cuenta desea ingresar.",
        };
      }

      const selected = accounts[0];

      setPendingAccounts([]);
      setActiveAccount(selected);
      setAccessToken(selected.access || "");
      setRefreshToken(selected.refresh || "");
      setUser(buildUserFromAccount(selected));
      persistActiveAccount(selected);

      return {
        ok: true,
        requiresSelection: false,
        redirectTo: selected.redirectTo,
        accounts,
        account: selected,
        message: "Sesión iniciada correctamente.",
      };
    },
    [buildUserFromAccount, clearAuthStorage, persistActiveAccount]
  );

  const selectAccount = useCallback(
    (type) => {
      const normalizedType = normalizeType(type);
      const selected = pendingAccounts.find((item) => item.type === normalizedType);

      if (!selected) {
        return {
          ok: false,
          redirectTo: "",
          message: "No se encontró la cuenta seleccionada.",
        };
      }

      setActiveAccount(selected);
      setPendingAccounts([]);
      setAccessToken(selected.access || "");
      setRefreshToken(selected.refresh || "");
      setUser(buildUserFromAccount(selected));
      persistActiveAccount(selected);

      return {
        ok: true,
        redirectTo: selected.redirectTo,
        account: selected,
        message: "Cuenta seleccionada correctamente.",
      };
    },
    [pendingAccounts, buildUserFromAccount, persistActiveAccount]
  );

  const logoutUser = useCallback(() => {
    setUser(emptyUser);
    setActiveAccount(null);
    setPendingAccounts([]);
    setAccessToken("");
    setRefreshToken("");
    setSections([]);
    setBrands([]);
    setCartItems([]);
    setIsCartOpen(false);

    if (!isBrowser) return;

    clearAuthStorage();
    localStorage.removeItem("cartItems");
  }, [clearAuthStorage]);

  const refreshAccessToken = useCallback(async () => {
    if (!isBrowser) return null;

    const storedSession = getStoredAuthSession();
    const currentAccount = storedSession?.activeAccount || activeAccount;
    const storedRefresh = currentAccount?.refresh || localStorage.getItem("refresh") || "";

    if (!storedRefresh) {
      logoutUser();
      return null;
    }

    try {
      const response = await fetch("/api/token/refresh/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh: storedRefresh }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.access) {
        logoutUser();
        return null;
      }

      const updatedAccount = currentAccount
        ? {
            ...currentAccount,
            access: data.access,
            refresh: data.refresh || currentAccount.refresh || storedRefresh,
          }
        : null;

      if (updatedAccount) {
        setActiveAccount(updatedAccount);
        setAccessToken(updatedAccount.access || "");
        setRefreshToken(updatedAccount.refresh || "");
        setUser(buildUserFromAccount(updatedAccount));
        persistActiveAccount(updatedAccount);
      }

      return data.access;
    } catch {
      logoutUser();
      return null;
    }
  }, [activeAccount, logoutUser, persistActiveAccount, buildUserFromAccount]);

  const authFetch = useCallback(
    async (url, options = {}) => {
      let token = activeAccount?.access || accessToken || "";

      const headers = new Headers(options.headers || {});

      if (token) headers.set("Authorization", `Bearer ${token}`);

      let response = await fetch(url, { ...options, headers });

      if (response.status === 401 && isBrowser) {
        const newAccess = await refreshAccessToken();

        if (!newAccess) return response;

        const retryHeaders = new Headers(options.headers || {});
        retryHeaders.set("Authorization", `Bearer ${newAccess}`);

        response = await fetch(url, {
          ...options,
          headers: retryHeaders,
        });
      }

      return response;
    },
    [activeAccount, accessToken, refreshAccessToken]
  );

  const addToCart = (product, quantity) => {
    setCartItems((prev) => {
      const existing = prev.find((i) => i.id === product.id);

      if (existing) {
        return prev.map((i) =>
          i.id === product.id
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      }

      return [...prev, { ...product, quantity }];
    });

    setIsCartOpen(true);
  };

  const removeFromCart = (id) =>
    setCartItems((prev) => prev.filter((i) => i.id !== id));

  const clearCart = () => setCartItems([]);

  const canAccessAdmin = useCallback(() => {
    return !!user.isLoggedIn && user.accountType === "admin";
  }, [user]);

  const canAccessClient = useCallback(() => {
    return !!user.isLoggedIn && user.accountType === "client";
  }, [user]);

  const canAccessPath = useCallback(
    (pathname) => {
      const path = String(pathname || "");

      if (path.startsWith("/admin-panel")) {
        return canAccessAdmin();
      }

      if (path.startsWith("/user-panel")) {
        return canAccessClient();
      }

      return true;
    },
    [canAccessAdmin, canAccessClient]
  );

  const getBlockedRedirect = useCallback(
    (pathname) => {
      const path = String(pathname || "");

      if (!user.isLoggedIn) {
        return "/login";
      }

      if (path.startsWith("/admin-panel") && user.accountType !== "admin") {
        return "/user-panel";
      }

      if (path.startsWith("/user-panel") && user.accountType !== "client") {
        return "/admin-panel";
      }

      return "/";
    },
    [user]
  );

  const refreshSections = useCallback(async () => {
    if (!user.isLoggedIn || user.accountType !== "admin") return;

    setSectionsLoading(true);

    try {
      const response = await authFetch("/categorias/secciones/");

      if (!response.ok) {
        setSections([]);
        return;
      }

      const data = await response.json();
      setSections(Array.isArray(data) ? data : []);
    } catch {
      setSections([]);
    } finally {
      setSectionsLoading(false);
    }
  }, [authFetch, user.isLoggedIn, user.accountType]);

  const refreshBrands = useCallback(async () => {
    if (!user.isLoggedIn || user.accountType !== "admin") return;

    setBrandsLoading(true);

    try {
      const response = await authFetch("/categorias/marcas/");

      if (!response.ok) {
        setBrands([]);
        return;
      }

      const data = await response.json();
      setBrands(Array.isArray(data) ? data : []);
    } catch {
      setBrands([]);
    } finally {
      setBrandsLoading(false);
    }
  }, [authFetch, user.isLoggedIn, user.accountType]);

  useEffect(() => {
    if (!user.isLoggedIn || user.accountType !== "admin") return;
    refreshSections();
    refreshBrands();
  }, [refreshSections, refreshBrands, user.isLoggedIn, user.accountType]);

  const contextValue = useMemo(
    () => ({
      user,
      setUser,
      activeAccount,
      pendingAccounts,
      hasPendingSelection: pendingAccounts.length > 1,
      accountType,
      isAuthenticated,
      isAdminAccount,
      isClientAccount,
      loginUser,
      loginClient,
      startLoginSession,
      selectAccount,
      logoutUser,
      accessToken,
      refreshToken,
      setAccessToken,
      setRefreshToken,
      authLoading,
      sections,
      setSections,
      brands,
      setBrands,
      refreshSections,
      refreshBrands,
      sectionsLoading,
      brandsLoading,
      cartItems,
      setCartItems,
      addToCart,
      removeFromCart,
      clearCart,
      isCartOpen,
      setIsCartOpen,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      csrfFetch: authFetch,
      authFetch,
      refreshAccessToken,
      canAccessAdmin,
      canAccessClient,
      canAccessPath,
      getBlockedRedirect,
    }),
    [
      user,
      activeAccount,
      pendingAccounts,
      accountType,
      isAuthenticated,
      isAdminAccount,
      isClientAccount,
      loginUser,
      loginClient,
      startLoginSession,
      selectAccount,
      logoutUser,
      accessToken,
      refreshToken,
      authLoading,
      sections,
      brands,
      sectionsLoading,
      brandsLoading,
      cartItems,
      isCartOpen,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      authFetch,
      refreshAccessToken,
      canAccessAdmin,
      canAccessClient,
      canAccessPath,
      getBlockedRedirect,
      refreshSections,
      refreshBrands,
    ]
  );

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};